import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { Clear, Folder, Search } from '@mui/icons-material';
import { ProjectSchemeInterface } from 'ts-interfaces';
import { useProjectSelection } from '../hooks/useProjectSelection';

interface ProjectSelectorProps {
  selectedProject: ProjectSchemeInterface | null;
  ownedProjects: ProjectSchemeInterface[];
  sharedProjects: ProjectSchemeInterface[];
}

export const ProjectSelector = ({ 
  selectedProject, 
  ownedProjects, 
  sharedProjects 
}: ProjectSelectorProps) => {

  // Simple validation to ensure selected project exists in available projects
  const allAvailableProjects = [...ownedProjects, ...sharedProjects];
  const selectedProjectId = selectedProject && allAvailableProjects.some(p => p.id === selectedProject.id) 
    ? selectedProject.id : "";

    const {
    searchTerm,
    dropdownOpen,
    hoveredProject,
    showMoreOwned,
    showMoreShared,
    searchInputRef,
    filteredOwnedProjects,
    filteredSharedProjects,
    displayedOwnedProjects,
    displayedSharedProjects,
    setDropdownOpen,
    setHoveredProject,
    setShowMoreOwned,
    setShowMoreShared,
    handleSearchChange,
    clearSearch,
    handleNavigateToProject,
    handleDropdownClose
  } = useProjectSelection(ownedProjects, sharedProjects, selectedProjectId);
  
  // Custom menu component that includes search
  const renderMenuItems = () => {
    const items: React.ReactNode[] = [];
    
    // Search field (not a MenuItem, so won't interfere with Select values)
    items.push(
      <Box key="search" sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
        <TextField
          fullWidth
          placeholder="Search projects..."
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          inputRef={searchInputRef}
          autoComplete='off'
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#999' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={clearSearch}
                  edge="end"
                >
                  <Clear sx={{ color: '#999' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </Box>
    );

    // My Projects Section
    if (filteredOwnedProjects.length > 0) {
      items.push(
        <Box key="owned-header" sx={{ pointerEvents: 'none' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              px: 2, 
              py: 1.5, 
              backgroundColor: '#f8f9fa', 
              fontWeight: 700,
              fontSize: '1.1rem',
              color: '#333',
              letterSpacing: '0.02em'
            }}
          >
            My Projects
          </Typography>
        </Box>
      );

      // Add project MenuItems directly
      displayedOwnedProjects.forEach((project) => {
        items.push(
          <MenuItem 
            key={project.id} 
            value={project.id} 
            onMouseEnter={() => setHoveredProject(project.id)}
            onMouseLeave={() => setHoveredProject(null)}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              pr: 2,
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
              cursor: 'pointer',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
              <Folder sx={{ mr: 1.5, color: '#666', fontSize: '1.2rem', flexShrink: 0 }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#333',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1
                }}
              >
                {project.name}
              </Typography>
            </Box>
            
            {hoveredProject === project.id && (
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigateToProject(project.id);
                }}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: '#000',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  px: 2,
                  py: 0.5,
                  minWidth: 'auto',
                  textTransform: 'none',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: '#333',
                  }
                }}
              >
                Open
              </Button>
            )}
          </MenuItem>
        );
      });

      // Show More button for owned projects
      if (!searchTerm && filteredOwnedProjects.length > 4) {
        items.push(
          <Box 
            key="owned-show-more"
            sx={{ 
              px: 2, 
              py: 1,
              textAlign: 'center',
              borderTop: '1px solid #f0f0f0',
              pointerEvents: 'auto'
            }}
          >
            <Button
              variant="text"
              size="small"
              onClick={() => setShowMoreOwned(!showMoreOwned)}
              sx={{
                fontSize: '0.8rem',
                textTransform: 'none',
                color: '#666',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#333'
                }
              }}
            >
              {showMoreOwned ? 'show less' : `show more (${filteredOwnedProjects.length - displayedOwnedProjects.length})`}
            </Button>
          </Box>
        );
      }
    }

    // Divider between sections
    if (filteredOwnedProjects.length > 0 && filteredSharedProjects.length > 0) {
      items.push(<Divider key="divider" />);
    }

    // Shared Projects Section
    if (filteredSharedProjects.length > 0) {
      items.push(
        <Box key="shared-header" sx={{ pointerEvents: 'none' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              px: 2, 
              py: 1.5, 
              backgroundColor: '#f8f9fa', 
              fontWeight: 700,
              fontSize: '1.1rem',
              color: '#333',
              letterSpacing: '0.02em'
            }}
          >
            Shared with Me
          </Typography>
        </Box>
      );

      // Add shared project MenuItems directly
      displayedSharedProjects.forEach((project) => {
        items.push(
          <MenuItem 
            key={project.id} 
            value={project.id} 
            onMouseEnter={() => setHoveredProject(project.id)}
            onMouseLeave={() => setHoveredProject(null)}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              pr: 2,
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
              cursor: 'pointer',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
              <Folder sx={{ mr: 1.5, color: '#666', fontSize: '1.2rem', flexShrink: 0 }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#333',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1
                }}
              >
                {project.name}
              </Typography>
            </Box>
            
            {hoveredProject === project.id && (
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigateToProject(project.id);
                }}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: '#000',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  px: 2,
                  py: 0.5,
                  minWidth: 'auto',
                  textTransform: 'none',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: '#333',
                  }
                }}
              >
                Open
              </Button>
            )}
          </MenuItem>
        );
      });

      // Show More button for shared projects
      if (!searchTerm && filteredSharedProjects.length > 4) {
        items.push(
          <Box 
            key="shared-show-more"
            sx={{ 
              px: 2, 
              py: 1,
              textAlign: 'center',
              borderTop: '1px solid #f0f0f0',
              pointerEvents: 'auto'
            }}
          >
            <Button
              variant="text"
              size="small"
              onClick={() => setShowMoreShared(!showMoreShared)}
              sx={{
                fontSize: '0.8rem',
                textTransform: 'none',
                color: '#666',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: '#333'
                }
              }}
            >
              {showMoreShared ? 'show less' : `show more (${filteredSharedProjects.length - displayedSharedProjects.length})`}
            </Button>
          </Box>
        );
      }
    }

    // No results message
    if (searchTerm && filteredOwnedProjects.length === 0 && filteredSharedProjects.length === 0) {
      items.push(
        <Box key="no-results" sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No projects found matching "{searchTerm}"
          </Typography>
        </Box>
      );
    }

    return items;
  };

  if (ownedProjects.length === 0 && sharedProjects.length === 0) {
    return null;
  }

  return (
    <FormControl sx={{ width: '320px' }}>
      <Select
        data-testid="project-selector"
        value={selectedProjectId}
        displayEmpty
        open={dropdownOpen}
        onOpen={() => setDropdownOpen(true)}
        onClose={handleDropdownClose}
        renderValue={(value) => {
          if (!value) return "Select a project";
          const project = [...ownedProjects, ...sharedProjects].find(p => p.id === value);
          return project?.name || "Select a project";
        }}
        sx={{ 
          bgcolor: 'white',
          '.MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
          onMouseDown: (e) => {
            // Prevent dropdown from closing when clicking on search
            e.preventDefault();
          }
        }}
      >
        {renderMenuItems()}
      </Select>
    </FormControl>
  );
};